import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../../config/config";

// Admin Login - Uses same endpoint but validates admin type
export const AdminLogin = createAsyncThunk(
  "adminAuth/login",
  async ({ values }, { rejectWithValue }) => {
    try {
      // Use the regular login endpoint
      const response = await axios.post(`${BASE_URL}/loginUser`, {
        email: values?.email,
        password: values?.password,
      });

      // Double-check on frontend that user is admin type
      const userData = response?.data?.data;
      if (userData && userData.type !== "admin") {
        return rejectWithValue({
          message: "Access denied. This login is for administrators only.",
        });
      }

      // Only return serializable data
      return { data: response.data, status: response.status };
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Login failed",
      });
    }
  }
);

// Validate Admin Session - Uses /admin/me endpoint
export const ValidateAdmin = createAsyncThunk(
  "adminAuth/validate",
  async (_, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem("ots_token") || localStorage.getItem("ots_token");
      if (!token) {
        return rejectWithValue({ message: "No token found" });
      }

      const response = await axios.get(`${BASE_URL}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Only return serializable data
      return { data: response.data, status: response.status };
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Validation failed",
      });
    }
  }
);

// Admin Logout
export const AdminLogout = createAsyncThunk(
  "adminAuth/logout",
  async (_, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem("ots_token") || localStorage.getItem("ots_token");

      // Call logout API to log activity
      await axios.post(
        `${BASE_URL}/adminLogout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Clear storage
      localStorage.removeItem("ots_token");
      localStorage.removeItem("name");
      localStorage.removeItem("user_email");
      localStorage.removeItem("is_register");
      localStorage.removeItem("is_active");
      localStorage.removeItem("admin_demo_mode");
      sessionStorage.removeItem("ots_token");
      sessionStorage.removeItem("is_register");

      return { success: true };
    } catch (error) {
      // Still clear storage even if API fails
      localStorage.clear();
      sessionStorage.clear();
      return { success: true };
    }
  }
);

const AdminAuth = createSlice({
  name: "adminAuth",
  initialState: {
    loading: false,
    admin: null,
    isAuthenticated: false,
    error: "",
  },
  reducers: {
    resetAdminAuth: (state) => {
      state.loading = false;
      state.admin = null;
      state.isAuthenticated = false;
      state.error = "";
    },
  },
  extraReducers: (builder) => {
    builder
      // Admin Login
      .addCase(AdminLogin.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(AdminLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.admin = action.payload?.data?.data;
        state.isAuthenticated = true;
        state.error = "";
      })
      .addCase(AdminLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Login failed";
        state.isAuthenticated = false;
      })
      // Validate Admin
      .addCase(ValidateAdmin.pending, (state) => {
        state.loading = true;
      })
      .addCase(ValidateAdmin.fulfilled, (state, action) => {
        state.loading = false;
        // Handle different response formats: { user: {...} } or { data: {...} } or { success: true, data: {...} }
        state.admin = action.payload?.data?.user || action.payload?.data?.data || action.payload?.data;
        state.isAuthenticated = true;
      })
      .addCase(ValidateAdmin.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.admin = null;
      })
      // Admin Logout
      .addCase(AdminLogout.fulfilled, (state) => {
        state.loading = false;
        state.admin = null;
        state.isAuthenticated = false;
        });
  },
});

export const { resetAdminAuth } = AdminAuth.actions;
export default AdminAuth.reducer;
